import { Link } from 'react-router-dom';
import { ArrowRight, Briefcase, Target, Users, Shield } from 'lucide-react';

const credentials = [
  {
    icon: Briefcase,
    title: '34+ Years of Business Leadership',
    description: 'Ownership, turnaround, and executive management across complex operational environments.',
  },
  {
    icon: Target,
    title: 'Deep Industry Expertise',
    description: 'Cemetery and deathcare operations, pre-need sales, and insurance distribution.',
  },
  {
    icon: Users,
    title: 'Board Experience',
    description: 'Nonprofit boards, family-owned operations, and private equity stakeholders.',
  },
  {
    icon: Shield,
    title: 'Operational Discipline',
    description: 'Measurable outcomes, governance alignment, and sustainable improvements.',
  },
];

export default function About() {
  return (
    <div>
      {/* Hero */}
      <section className="bg-slate-900 text-white py-12 md:py-16">
        <div className="container-custom">
          <div className="max-w-3xl">
            <h2 className="font-serif text-3xl md:text-4xl font-medium mb-4">
              About Todd Sexton
            </h2>
            <p className="text-lg text-gray-300">
              Operator, not theorist. Deep experience in cemetery and deathcare operations.
            </p>
          </div>
        </div>
      </section>

      {/* Biography */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 max-w-content mx-auto">
            <div className="lg:col-span-7">
              <h2 className="font-serif text-xl md:text-2xl font-medium text-slate-900 mb-5">
                Operator, Not Theorist
              </h2>
              
              <div className="space-y-4 text-slate-600 leading-relaxed text-sm">
                <p>
                  Over 50 years of owning, operating, restructuring, and exiting businesses — including 
                  deep experience in cemetery and deathcare operations.
                </p>
                
                <p>
                  Todd has led distressed acquisitions, nonprofit turnarounds, large-scale pre-need 
                  sales organizations, regulatory remediation efforts, and technology modernization 
                  initiatives. He works from inside the operation — not from the sidelines.
                </p>
                
                <p>
                  His expertise spans revenue system development, pre-need architecture, governance 
                  alignment, regulatory compliance, and long-term asset value enhancement.
                </p>
                
                <p>
                  Todd works selectively with organizations ready for meaningful transformation — 
                  family-owned cemeteries, nonprofit boards, funeral home ownership groups, and 
                  private equity firms seeking to unlock value in cemetery assets.
                </p>

                <p>
                  Todd limits concurrent engagements to maintain depth, discretion, and meaningful 
                  operational involvement. Clients work directly with him — not a junior team.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-gray-50 p-6 border border-gray-100">
                <h3 className="font-serif text-lg font-medium text-slate-900 mb-5">
                  Professional Credentials
                </h3>
                <div className="space-y-4">
                  {credentials.map((cred, index) => (
                    <div key={index} className="flex gap-3">
                      <cred.icon className="w-6 h-6 text-accent-600 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-slate-900 mb-0.5 text-sm">
                          {cred.title}
                        </h4>
                        <p className="text-xs text-slate-600">
                          {cred.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Positioning Statement */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-serif text-xl md:text-2xl font-medium mb-4">
              Brand Positioning
            </h2>
            <p className="text-lg text-gray-300 italic">
              "Todd Sexton provides experienced fractional COO leadership to cemetery and 
              deathcare organizations seeking operational stability, revenue expansion, 
              and long-term asset strength."
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom text-center">
          <h2 className="font-serif text-2xl font-medium text-slate-900 mb-4">
            Ready to Discuss Your Organization?
          </h2>
          <p className="text-slate-600 max-w-xl mx-auto mb-8">
            If your organization is facing operational complexity, revenue challenges, 
            or preparing for transition, let's have a confidential conversation.
          </p>
          <Link to="/contact" className="btn-primary">
            Schedule a Confidential Consultation
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
